import React, { useEffect, useState, useRef } from "react";
import "./Gut.scss";
import ReactHtmlParser from "react-html-parser";
import {
    loadGutAssessmentData,
    sortGutQuestions,
    buildGutAnswerData,
    submitGutAssessmentAnswer,
    getGutResult,
    preloadImage,
} from "./Gut.service";

function Gut() {
    const [accepted, setAccepted] = useState(false);
    const [assessmentData, setAssessmentData] = useState(null);
    const [tcData, setTCData] = useState(null);
    const [showTC, setShowTC] = useState(false);
    const [fontReady, setFontReady] = useState(false);
    const [bannerReady, setBannerReady] = useState(false);
    const [started, setStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswering, setIsAnswering] = useState(false);
    const [resultScore, setResultScore] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [pendingQuestion, setPendingQuestion] = useState(null);
    const [hasHistory, setHasHistory] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    const [selectedHistory, setSelectedHistory] = useState(0);
    const [open, setOpen] = useState(false);
    const [stickerPos, setStickerPos] = useState({
        x: window.innerWidth - 140,
        y: window.innerHeight - 200,
    });
    const [showSticker, setShowSticker] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const bannerUrl = assessmentData?.landing_image_url
        ? `${process.env.REACT_APP_S3_URL}${assessmentData.landing_image_url}`
        : '';
    const breakContent = {
        5: {
            pic: "/images/gut/guttest_break5.png",
            title: (
                <>
                    การรับรู้ความรู้สึกของตัวเอง... <br />
                    คือจุดเริ่มต้นของการดูแลใจ
                </>
            ),
            desc: "คุณทำได้ดีแล้ว ไปกันต่อเลย",
        },
        15: {
            pic: "/images/gut/guttest_break15.png",
            title: (
                <>
                    คุณทำได้ดีมาก
                    <br />
                    ใกล้ครบทุกข้อแล้ว
                </>
            ),
            desc: "อีกนิดเดียว",
        },
    };

    const currentBreak = breakContent[pendingQuestion];

    const questions = sortGutQuestions(assessmentData);
    console.log("questions", questions)
    const question = questions[currentQuestion];

    const progress = questions.length
        ? Math.round(((currentQuestion) / (questions.length - 1)) * 100)
        : 0;

    const result = resultScore !== null
        ? getGutResult(resultScore, assessmentData)
        : null;

    const handleStart = () => {
        if (!accepted) return;
        setStarted(true);
    };

    const handleBack = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        } else {
            setStarted(false);
        }
    };

    const reloadData = async () => {
        try {
            const { assessment, tc, logHistory, hasHistory } =
                await loadGutAssessmentData();

            setHistoryList(logHistory);
            setHasHistory(hasHistory);

            if (hasHistory) {
                setAccepted(true);
            }

            setAssessmentData(assessment);
            setTCData(tc);
        } catch (error) {
            console.error(error);
        }
    };

    const resetAssessment = async () => {
        setStarted(false);
        setShowResult(false);
        setCurrentQuestion(0);
        setAnswers([]);
        setResultScore(null);
        setPendingQuestion(null);
        setShowSticker(false);
        await reloadData();
    };

    const retryAssessment = () => {
        setShowResult(false);
        setCurrentQuestion(0);
        setAnswers([]);
        setResultScore(null);
        setPendingQuestion(null);
        setShowSticker(false);
        setStarted(true);
    };

    const submitGutAssessment = async (answerList) => {
        try {
            const { totalScore } = await submitGutAssessmentAnswer(
                assessmentData?._id,
                answerList
            );
            await reloadData();
            setResultScore(totalScore);
            setShowResult(true);
            setShowSticker(true);
        } catch (error) {
            console.error(error);
        }
    };

    const answeringRef = useRef(false);

    const handleAnswer = async (answer) => {
        if (answeringRef.current || !question) return;

        answeringRef.current = true;

        setSelectedAnswer(answer.value);
        setIsAnswering(true);

        const answerData = buildGutAnswerData(question, answer);

        const newAnswers = [
            ...answers.filter(item => item.question_id !== question._id),
            answerData,
        ];

        setAnswers(newAnswers);

        setTimeout(async () => {
            setSelectedAnswer(null);
            setIsAnswering(false);

            const nextQuestion = currentQuestion + 1;

            if (nextQuestion < questions.length) {

                setCurrentQuestion(nextQuestion);
                answeringRef.current = false;
            } else {
                await submitGutAssessment(newAnswers);
                answeringRef.current = false;
            }
        }, 300);
    };

    const handleStickerPointerDown = (e) => {
        e.preventDefault();

        const startX = e.clientX;
        const startY = e.clientY;
        const initialX = stickerPos.x;
        const initialY = stickerPos.y;

        let moved = false;

        const move = (ev) => {
            moved = true;
            setIsDragging(true);

            const size = 100;

            let x = initialX + (ev.clientX - startX);
            let y = initialY + (ev.clientY - startY);

            x = Math.max(0, Math.min(window.innerWidth - size, x));
            y = Math.max(0, Math.min(window.innerHeight - size, y));

            setStickerPos({ x, y });
        };

        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);

            setTimeout(() => {
                setIsDragging(false);
            }, 50);
        };

        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };

    useEffect(() => {
        let mounted = true;

        const waitFont = async () => {
            try {
                if (document.fonts) {
                    await Promise.race([
                        document.fonts.ready,
                        new Promise(resolve => setTimeout(resolve, 1000)),
                    ]);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (mounted) setFontReady(true);
            }
        };

        waitFont();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        reloadData();
    }, []);

    useEffect(() => {
        if (!assessmentData?.landing_image_url) return;

        const bannerUrl = `${process.env.REACT_APP_S3_URL}${assessmentData.landing_image_url}`;

        preloadImage(bannerUrl).finally(() => {
            setBannerReady(true);
        });
    }, [assessmentData]);


    // useEffect(() => {
    //     if (!bannerUrl) return;

    //     setBannerReady(false);

    //     const img = new Image();

    //     img.onload = () => {
    //         setBannerReady(true);
    //     };

    //     img.onerror = () => {
    //         setBannerReady(true); // กันค้างโหลด ถ้ารูปเสีย
    //     };

    //     img.src = bannerUrl;
    // }, [bannerUrl]);

    if (!fontReady || !assessmentData || !tcData || !bannerReady) {
        return <div className="Gut bgGutLoading" />;
    }
    return (
        <div className="Gut">
            {showResult ? (
                <div className={`gutResultPage ${result?.className || ""}`}>
                    <div className="gutResultContent">
                        <h2 className="gutResultTitle">แบบประเมินสุขภาพลำไส้</h2>

                        {hasHistory && historyList.length > 0 && (
                            <div className="gutHistorySelectWrap">
                                <div className="dropdown">
                                    <button
                                        type="button"
                                        className="dropdown__header"
                                        onClick={() => setOpen(!open)}
                                    >
                                        <span>{historyList?.[selectedHistory]?.createdDate}</span>
                                        <span className={`arrow ${open ? "open" : ""}`} />
                                    </button>

                                    {open && (
                                        <div className="dropdown__list">
                                            {historyList.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className="dropdown__item"
                                                    onClick={() => {
                                                        setSelectedHistory(index);
                                                        setResultScore(item?.result || 0);
                                                        setOpen(false);
                                                    }}
                                                >
                                                    {item.createdDate}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <p className="gutResultSubTitle">คะแนนของคุณ</p>
                        {result && (
                            <>
                                <div className={`gutScoreBox ${result?.className || ""}`}>
                                    <img
                                        className="gutScoreIcon"
                                        src={`/images/gut/gut-score-icon${'-' + result.className}.png`}
                                        alt=""
                                    />

                                    <strong>{resultScore}</strong>
                                </div>

                                <div className={`gutResultCard ${result?.className || ""}`}>
                                    <h3>{result.detail}</h3>

                                    <div className={`gutResultRange ${result?.className || ""}`}>
                                        คะแนน {result.range}
                                    </div>

                                    <p>{ReactHtmlParser(result.desc)}</p>

                                    <div className="gutSuggestList">
                                        <div className="gutSuggestItem">
                                            <img
                                                src={`/images/gut/gut-shield-icon${'-' + result.className}.png`}
                                                alt=""
                                            />

                                            <div>
                                                <h4>{result.optionFirst}</h4>
                                                <p>{result.optionFirstDetail}</p>
                                            </div>
                                        </div>

                                        <div className="gutSuggestItem">
                                            <img
                                                src={`/images/gut/gut-salad-icon${'-' + result.className}.png`}
                                                alt=""
                                            />

                                            <div>
                                                <h4>{result.optionSecond}</h4>
                                                <p>{result.optionSecondDetail}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="resultGutBottom">
                        <button
                            type="button"
                            className="resultGutRetryBtn"
                            onClick={resetAssessment}
                        >
                            ← กลับสู่หน้าหลัก
                        </button>


                    </div>
                </div>
            ) : !started ? (
                <div className="gutHome" >



                    <section className="gutBanner">
                        {assessmentData?.landing_image_url && (
                            <img
                                className="gutBannerFullImg"
                                src={`${process.env.REACT_APP_S3_URL}${assessmentData?.landing_image_url}`}
                                alt="Gut Banner"
                            />
                        )}
                    </section>



                    <div className="gutBottom">
                        {!hasHistory ? (
                            <>
                                <label className="gutAccept">
                                    <input
                                        type="checkbox"
                                        checked={accepted}
                                        onChange={(e) => setAccepted(e.target.checked)}
                                    />

                                    <span className="gutCheck" />

                                    <span
                                        className="gutAcceptText"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowTC(true);
                                        }}
                                    >
                                        ยอมรับเงื่อนไขการทำแบบประเมินความเครียด
                                    </span>
                                </label>

                                <button
                                    type="button"
                                    className={`gutStartBtn ${accepted ? "active" : ""}`}
                                    disabled={!accepted}
                                    onClick={handleStart}
                                >
                                    เริ่มทำแบบประเมิน <span>→</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="gutStartBtn active"
                                    onClick={handleStart}
                                >
                                    เริ่มทำแบบประเมิน <span>→</span>
                                </button>

                                <button
                                    type="button"
                                    className="gutHistoryBtn"
                                    onClick={() => {
                                        const item = historyList[0];

                                        setSelectedHistory(0);
                                        setResultScore(item?.result || 0);
                                        setShowSticker(true);
                                        setShowResult(true);
                                    }}
                                >
                                    ดูประวัติการทำแบบประเมิน
                                </button>
                            </>
                        )}
                    </div>
                </div>

            ) : (
                <div className="gutQuestionPage">
                    {question?.image_url && (
                        <img
                            className="questionGutImage"
                            src={`${process.env.REACT_APP_S3_URL}${question.image_url}`}
                            alt={question.question}
                        />
                    )}
                    <div className="gutQuestionTop">
                        <h1>แบบประเมินสุขภาพลำไส้</h1>
                    </div>



                    <div className="questionGutCard">
                        <h2>คำถามที่ {question?.display_order}</h2>
                        <h3>{ReactHtmlParser(question?.question)}</h3>
                    </div>

                    <div className="answerGutGroup">
                        {(question?.question_value || []).map((item) => (
                            <button
                                key={item._id || item.label}
                                type="button"
                                className={`answerGutCard ${selectedAnswer === item.score ? "selected" : ""
                                    }`}
                                onClick={() =>
                                    !isAnswering &&
                                    handleAnswer({
                                        text: item.answer,
                                        value: item.score,
                                    })
                                }
                            >
                                <div className="answerGutLeft">

                                    <span className="answerGutText">
                                        {item.answer}
                                    </span>
                                </div>


                            </button>
                        ))}
                    </div>

                    <div className="questionGutFooter">
                        <button
                            type="button"
                            className="backGutBtn"
                            onClick={handleBack}
                        >
                            <span>←</span>
                        </button>

                        <div className="progressGutWrap">
                            <div className="progressGutBar">
                                <div
                                    className="progressGutFill"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>

                            <span>{currentQuestion + 1}/{questions.length}</span>
                        </div>
                    </div>
                </div>
            )}

            {showTC && (
                <div
                    className="gutTcOverlay"
                    onClick={() => setShowTC(false)}
                >
                    <div
                        className="gutTcModal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="gutTcHandle" />

                        <div className="gutTcContent">
                            {ReactHtmlParser(tcData?.data?.content)}
                        </div>

                        <button
                            type="button"
                            className="gutTcAcceptBtn"
                            onClick={() => {
                                setAccepted(true);
                                setShowTC(false);
                            }}
                        >
                            ยอมรับเงื่อนไข
                        </button>
                    </div>
                </div>
            )}

            {showSticker && (
                <div
                    className="gutFloatingSticker"
                    style={{
                        left: `${stickerPos.x}px`,
                        top: `${stickerPos.y}px`,
                    }}
                    onPointerDown={handleStickerPointerDown}
                    onClick={() => {
                        window.open(assessmentData?.metaData?.redirectURL, "_blank");
                    }}
                >
                    <button
                        type="button"
                        aria-label="close"
                        className="gutFloatingStickerClose"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowSticker(false);
                        }}
                    />

                    <img
                        src={
                            assessmentData?.metaData?.redirectImage
                                ? `${process.env.REACT_APP_S3_URL}${assessmentData.metaData.redirectImage}`
                                : "/images/stress/stresstest_heart.png"
                        }
                        alt=""
                    />
                </div>
            )}
        </div>
    );
}

export default Gut;